/**
 * Photo Journal tag catalog — shared source of truth.
 *
 * Used by both kra-photo-journal (tag editor / filter UI) and kra-admin
 * (photographer adds tags when sending photos to customer).
 *
 * Tags are Airtable multi-select fields on table "💪🏼Quản lý ảnh khách".
 */

export const TAG_FIELD_NAMES = [
  'kimono_tag',
  'kimono_color_tag',
  'season_tag',
  'shoot_tag',
  'scene_tag',
] as const;

export type TagFieldName = (typeof TAG_FIELD_NAMES)[number];

export type TagGroupKey =
  | 'kimonoTags'
  | 'kimonoColorTags'
  | 'seasonTags'
  | 'shootTags'
  | 'sceneTags';

export interface TagGroup {
  /** Key on the normalised PhotoRecord. */
  key: TagGroupKey;
  /** Airtable field name. */
  fieldName: TagFieldName;
  /** Display label (Vietnamese). */
  label: string;
  /** Emoji prefix for visual grouping. */
  emoji: string;
}

export const TAG_GROUPS: readonly TagGroup[] = [
  { key: 'kimonoTags',      fieldName: 'kimono_tag',       label: 'Trang phục',  emoji: '👘' },
  { key: 'kimonoColorTags', fieldName: 'kimono_color_tag', label: 'Màu sắc',     emoji: '🎨' },
  { key: 'seasonTags',      fieldName: 'season_tag',       label: 'Mùa',         emoji: '🌸' },
  { key: 'shootTags',       fieldName: 'shoot_tag',        label: 'Kiểu chụp',   emoji: '📷' },
  { key: 'sceneTags',       fieldName: 'scene_tag',        label: 'Cảnh',        emoji: '🏯' },
];

/** Partial map of Airtable field name → selected tag values. */
export type TagSet = Partial<Record<TagFieldName, string[]>>;

/** Options map (field name → all available option names from Airtable schema). */
export type TagOptions = Record<TagFieldName, string[]>;

export const EMPTY_TAG_SET: TagSet = {};

/** Returns true when the tag set has at least one selected tag. */
export function hasAnyTag(tags: TagSet | undefined): boolean {
  if (!tags) return false;
  return TAG_FIELD_NAMES.some((f) => (tags[f]?.length ?? 0) > 0);
}

/** Total number of selected tags across all groups. */
export function countTags(tags: TagSet | undefined): number {
  if (!tags) return 0;
  return TAG_FIELD_NAMES.reduce((sum, f) => sum + (tags[f]?.length ?? 0), 0);
}

/** Minimum number of tags a photographer must attach before delivering. */
export const MIN_PHOTO_TAGS = 2;

/**
 * Tag groups a photographer MUST fill before a set counts as complete.
 *
 * Business rule: outfit (Trang phục) + colour (Màu sắc) + scene (Cảnh) are
 * mandatory; season (Mùa) + shoot-type (Kiểu chụp) stay optional. Enforced at
 * every surface where staff attach tags — the kra-admin delivery gate and the
 * kra-photo-journal quick tag editor — so the rule lives here, once.
 */
export const REQUIRED_TAG_GROUP_KEYS: readonly TagGroupKey[] = [
  'kimonoTags',      // Trang phục
  'kimonoColorTags', // Màu sắc
  'sceneTags',       // Cảnh
];

/** True when the given group must be filled before a set is complete. */
export function isRequiredTagGroup(key: TagGroupKey): boolean {
  return REQUIRED_TAG_GROUP_KEYS.includes(key);
}

/**
 * Returns the required TAG_GROUPS that have no selected tag in `tags` (keyed by
 * Airtable field name). An empty result means every mandatory group is filled.
 */
export function getMissingRequiredTagGroups(tags: TagSet | undefined): TagGroup[] {
  return TAG_GROUPS.filter(
    (g) => isRequiredTagGroup(g.key) && (tags?.[g.fieldName]?.length ?? 0) === 0,
  );
}

// ---------------------------------------------------------------------------
// Indonesian guests — a shoot_tag value the shop actively invests in
// ---------------------------------------------------------------------------

/**
 * The `shoot_tag` value that marks a set as an Indonesian guest's.
 *
 * Business rule (2026-10): the shop is investing in the Indonesian market, so
 * every delivered set from an Indonesian guest MUST carry this tag — it is
 * how Photo Journal / marketing find reference sets for that audience. It is
 * stored as an ordinary `shoot_tag` option (no schema change), but UIs lift
 * it OUT of the "Kiểu chụp" chip row into its own yes/no question so staff
 * cannot overlook it next to "couple".
 */
export const INDONESIA_SHOOT_TAG = 'Indonesia';

/** True when the set is already tagged as an Indonesian guest's. */
export function hasIndonesiaTag(tags: TagSet | undefined): boolean {
  return (tags?.shoot_tag ?? []).includes(INDONESIA_SHOOT_TAG);
}

/** Returns a copy of `tags` with the Indonesia shoot_tag added or removed. */
export function setIndonesiaTag(tags: TagSet | undefined, on: boolean): TagSet {
  const current = (tags?.shoot_tag ?? []).filter((t) => t !== INDONESIA_SHOOT_TAG);
  return {
    ...(tags ?? {}),
    shoot_tag: on ? [...current, INDONESIA_SHOOT_TAG] : current,
  };
}

/**
 * Booking signals used to pre-answer "Khách Indonesia?" for staff.
 * `whatsapp` should already be normalised to international digits
 * (e.g. via core-booking `normalizeWhatsApp`); local "08…" numbers are
 * NOT recognised here.
 */
export interface IndonesianGuestSignals {
  /** Booking `Language` (e.g. "id", "Bahasa Indonesia", "en"). */
  language?: string | null;
  /** Booking `WhatsApp`, ideally normalised to "62…". */
  whatsapp?: string | null;
  /** Booking display name (walk-ins are often "Khách Indonesia 1234"). */
  name?: string | null;
}

/**
 * Guesses whether a booking belongs to an Indonesian guest from cheap
 * signals, so the tag UI can pre-select "Có" and explain why. Staff can
 * always override — this is a hint, not the truth.
 */
export function guessIndonesianGuest(
  s: IndonesianGuestSignals,
): { isIndonesian: boolean; reason: string | null } {
  const lang = (s.language ?? '').trim().toLowerCase();
  if (lang === 'id' || lang.startsWith('id-') || lang.includes('indonesia')) {
    return { isIndonesian: true, reason: 'booking bằng tiếng Indonesia' };
  }
  const digits = (s.whatsapp ?? '').replace(/\D/g, '').replace(/^00/, '');
  if (digits.startsWith('62') && digits.length >= 10) {
    return { isIndonesian: true, reason: 'SĐT WhatsApp +62' };
  }
  if ((s.name ?? '').toLowerCase().includes('indonesia')) {
    return { isIndonesian: true, reason: 'tên booking có "Indonesia"' };
  }
  return { isIndonesian: false, reason: null };
}
